import React, { useState, Children } from 'react';
import { Home as HomeIcon, Search, SlidersHorizontal, Calendar as CalendarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import { FilterModal, FilterState } from '../components/FilterModal';
import { DateRangePicker } from '../components/DateRangePicker';
import { getAllListings, getListingsByCategory } from '../utils/mockListings';
interface BrowseListingsProps {
  category?: string;
  onBack: () => void;
  onItemClick: (id: string) => void;
}
export function BrowseListings({
  category,
  onBack,
  onItemClick
}: BrowseListingsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    priceRange: [0, 100],
    depositRange: [0, 100],
    distanceRange: [0, 10],
    minRating: 0,
    conditions: []
  });
  // Get listings based on category
  const allListings = category ? getListingsByCategory(category) : getAllListings();
  // Filter listings based on search and filters
  const filteredListings = allListings.filter(listing => {
    // Search filter
    if (searchQuery && !listing.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    // Price filter
    if (listing.pricing.day < filters.priceRange[0] || listing.pricing.day > filters.priceRange[1]) {
      return false;
    }
    // Rating filter
    if (filters.minRating > 0 && listing.rating < filters.minRating) {
      return false;
    }
    // Condition filter
    if (filters.conditions.length > 0 && !filters.conditions.some(c => listing.condition.includes(c))) {
      return false;
    }
    return true;
  });
  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
  };
  const handleDateSelect = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
  };
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-cream pb-24" data-id="element-691">
      {/* Header */}
      <div className="bg-white border-b border-sand sticky top-0 z-10" data-id="element-692">
        <div className="max-w-md mx-auto px-4 pt-4 pb-3" data-id="element-693">
          <div className="flex items-center justify-between mb-4" data-id="element-694">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-sand/20 rounded-full transition-colors" data-id="element-695">
              <HomeIcon size={24} className="text-charcoal" data-id="element-696" />
            </button>
            <Logo variant="full" size="md" data-id="element-697" />
            <button onClick={() => setShowFilters(true)} className="p-2 hover:bg-sand/20 rounded-full transition-colors relative" data-id="element-698">
              <SlidersHorizontal size={24} className="text-charcoal" data-id="element-699" />
              {(filters.minRating > 0 || filters.conditions.length > 0) && <span className="absolute top-1 right-1 w-2 h-2 bg-dark-green rounded-full" data-id="element-700" />}
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-3" data-id="element-701">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-moss-gray" size={20} data-id="element-702" />
            <input type="text" placeholder="I want to borrow..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-11 pr-4 py-3 bg-cream rounded-ios text-charcoal placeholder-moss-gray focus:outline-none focus:ring-2 focus:ring-dark-green" data-id="element-703" />
          </div>

          {/* Date Range Selector */}
          <button onClick={() => setShowDatePicker(true)} className="w-full flex items-center gap-2 px-4 py-3 bg-cream rounded-ios text-left hover:bg-sand/30 transition-colors" data-id="element-704">
            <CalendarIcon size={18} className="text-moss-gray" data-id="element-705" />
            <span className="text-sm text-charcoal" data-id="element-706">
              {startDate && endDate ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}` : 'Dates requested (optional)'}
            </span>
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6" data-id="element-707">
        {/* Category Headline */}
        {category && <div className="mb-6" data-id="element-708">
            <div className="bg-white border-2 border-sand rounded-ios-lg py-4 px-6 text-center shadow-sm" data-id="element-709">
              <h2 className="text-xl font-semibold text-charcoal" data-id="element-710">
                {category}
              </h2>
              <p className="text-sm text-moss-gray mt-1" data-id="element-711">
                {filteredListings.length}{' '}
                {filteredListings.length === 1 ? 'item' : 'items'} available
              </p>
            </div>
          </div>}

        {/* Listings Grid */}
        <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-2 gap-4" data-id="element-712">
          {filteredListings.map(listing => <motion.button key={listing.id} variants={item} whileTap={{
          scale: 0.98
        }} onClick={() => onItemClick(listing.id)} className="bg-white border border-sand/50 rounded-ios-lg overflow-hidden shadow-md hover:shadow-lg active:shadow-sm transition-all" data-id="element-713">
              <div className="aspect-square relative" data-id="element-714">
                <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover" data-id="element-715" />
                <div className="absolute bottom-2 right-2 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-charcoal" data-id="element-716">
                  {listing.location.replace('Within ', '')}
                </div>
              </div>
              <div className="p-3 text-left" data-id="element-717">
                <h3 className="font-semibold text-charcoal text-sm mb-2 line-clamp-2 leading-tight" data-id="element-718">
                  {listing.title}
                </h3>
                <div className="flex items-center justify-between mb-2" data-id="element-719">
                  <div data-id="element-720">
                    <div className="text-dark-green font-bold text-base leading-none" data-id="element-721">
                      ${listing.pricing.day}
                    </div>
                    <div className="text-xs text-moss-gray mt-0.5" data-id="element-722">per day</div>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-723">
                    <span className="text-clay text-sm" data-id="element-724">★</span>
                    <span className="text-xs font-medium text-charcoal" data-id="element-725">
                      {listing.rating}
                    </span>
                    <span className="text-xs text-moss-gray" data-id="element-726">
                      ({listing.reviewCount})
                    </span>
                  </div>
                </div>
              </div>
            </motion.button>)}
        </motion.div>

        {/* No Results */}
        {filteredListings.length === 0 && <div className="text-center py-12" data-id="element-727">
            <p className="text-moss-gray" data-id="element-728">
              No items found matching your filters
            </p>
          </div>}

        {/* Scroll Indicator */}
        {filteredListings.length > 4 && <div className="mt-6 text-center" data-id="element-729">
            <div className="inline-flex items-center gap-2 text-sm text-moss-gray" data-id="element-730">
              <span data-id="element-731">Scroll for more</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-732">
                <path d="M8 3V13M8 13L12 9M8 13L4 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-733" />
              </svg>
            </div>
          </div>}
      </div>

      {/* Filter Modal */}
      <FilterModal isOpen={showFilters} onClose={() => setShowFilters(false)} onApply={handleApplyFilters} data-id="element-734" />

      {/* Date Range Picker */}
      <DateRangePicker isOpen={showDatePicker} onClose={() => setShowDatePicker(false)} onSelect={handleDateSelect} startDate={startDate} endDate={endDate} data-id="element-735" />
    </div>;
}